<template>
  <div id="top-nav" class="d-print-none">
    <nav class="navbar fixed-top navbar-expand-xl navbar-light">
      <div class="container">
        <router-link class="navbar-brand" :to="{ name: 'Home' }">
          <font-awesome-icon fixed-width icon="calendar-alt" />
          <span class="d-none d-md-inline">CoActive </span>Rota
        </router-link>
        <button
          class="btn navbar-toggler order-last"
          type="button"
          data-bs-toggle="collapse"
          data-bs-target="#navbarSupportedContent"
          aria-controls="navbarSupportedContent"
          aria-expanded="false"
          aria-label="Toggle navigation"
        >
          <font-awesome-icon icon="bars" size="sm" />
        </button>
        <div id="navbarSupportedContent" class="collapse navbar-collapse">
          <ul class="navbar-nav">
            <top-nav-rota-dropdown />
            <b-nav-item-dropdown
              v-if="
                authStore.isSignedIn && $can('read', 'rota.rota-activities')
              "
              variant="link"
              toggle-class="text-decoration-none"
            >
              <template #button-content>
                <font-awesome-icon fixed-width icon="clipboard-list" />
                Activities
              </template>
              <b-dropdown-item
                v-if="
                  authStore.isSignedIn && $can('create', 'rota.rota-activities')
                "
                @click="showAddActivityPopup"
              >
                <font-awesome-icon fixed-width icon="plus" />
                Add a new activity
              </b-dropdown-item>
              <div
                v-if="
                  authStore.isSignedIn && $can('update', 'rota.rota-activities')
                "
                class="dropdown-divider"
              />
              <b-dropdown-item
                :to="{
                  name: 'activity/RotaActivityDay',
                  query: {
                    day: formatISO(new Date(), { representation: 'date' }),
                  },
                }"
              >
                <font-awesome-icon fixed-width icon="calendar-day" />
                Today's Activities &amp; appointments
              </b-dropdown-item>
              <b-dropdown-item :to="{ name: 'activity/RotaActivityList' }">
                <font-awesome-icon fixed-width icon="clipboard-list" />
                All Activities &amp; appointments
              </b-dropdown-item>
            </b-nav-item-dropdown>
            <b-nav-item-dropdown
              v-if="authStore.isSignedIn && $can('read', 'clients')"
              variant="link"
              toggle-class="text-decoration-none"
            >
              <template #button-content>
                <font-awesome-icon fixed-width icon="house-user" />
                Clients
              </template>
              <b-dropdown-item :to="{ name: 'client/ClientList' }">
                <font-awesome-icon fixed-width icon="house-user" />
                Client List
              </b-dropdown-item>
            </b-nav-item-dropdown>
            <b-nav-item-dropdown
              v-if="$can('read', 'rota.rota-reporting') && authStore.isSignedIn"
              variant="link"
              toggle-class="text-decoration-none"
            >
              <template #button-content>
                <font-awesome-icon fixed-width icon="table" />
                Manage
              </template>
              <template v-if="$can('read', 'rota.payrolls')">
                <li>
                  <h6 class="dropdown-header">Management</h6>
                </li>
                <b-dropdown-item :to="{ name: 'payroll' }">
                  <font-awesome-icon fixed-width icon="calendar-alt" />
                  Payroll
                </b-dropdown-item>
                <div class="dropdown-divider" />
              </template>
              <li>
                <h6 class="dropdown-header">Reports</h6>
              </li>
              <b-dropdown-item :to="{ name: 'report/ReportActivityReports' }">
                <font-awesome-icon fixed-width icon="clipboard-list" />
                Client Activity Reporting (Rota only)
              </b-dropdown-item>
              <b-dropdown-item :to="{ name: 'report/ReportClientActivities' }">
                <font-awesome-icon fixed-width icon="house-user" />
                Client Activities (PCS + Rota)
              </b-dropdown-item>
              <b-dropdown-item :to="{ name: 'report/ReportClientBehaviour' }">
                <font-awesome-icon fixed-width icon="person-burst" />
                Behaviour Logs
              </b-dropdown-item>
              <b-dropdown-item
                :to="{ name: 'report/ReportDeclinedActivities' }"
              >
                <font-awesome-icon fixed-width icon="user-slash" />
                Declined Activities
              </b-dropdown-item>
              <b-dropdown-item :to="{ name: 'report/ReportClientExperience' }">
                <font-awesome-icon fixed-width icon="face-smile-beam" />
                Client Experience
              </b-dropdown-item>
              <b-dropdown-item :to="{ name: 'report/ReportPayrollView' }">
                <font-awesome-icon fixed-width icon="users" />
                Rota Shifts for Payroll
              </b-dropdown-item>
              <template v-if="$can('manage', 'rota.rotas')">
                <div class="dropdown-divider" />
                <li>
                  <h6 class="dropdown-header">Dashboards</h6>
                </li>
                <b-dropdown-item :to="{ name: 'operations/Dashboard' }">
                  <font-awesome-icon fixed-width icon="gauge" />
                  Operations
                </b-dropdown-item>
                <b-dropdown-item :to="{ name: 'management/Dashboard' }">
                  <font-awesome-icon fixed-width icon="chart-line" />
                  Performance Management
                </b-dropdown-item>
                <b-dropdown-item
                  :to="{ name: 'management/Dashboard/DailyPerformance' }"
                >
                  <font-awesome-icon fixed-width icon="calendar-day" />
                  Daily Performance
                </b-dropdown-item>
              </template>
            </b-nav-item-dropdown>
          </ul>
          <ul
            v-if="authStore.isSignedIn"
            class="navbar-nav ms-auto mb-2 mb-lg-0"
          >
            <b-nav-item-dropdown
              v-if="
                $can('manage', 'rota.rota-users') ||
                $can('manage', 'rota.rota-agencies')
              "
              :right="true"
              variant="link"
              toggle-class="text-decoration-none"
            >
              <template #button-content> Setup </template>
              <b-dropdown-item
                v-if="$can('manage', 'rota.rota-users')"
                :to="{ name: 'setup/RotaUserList' }"
              >
                <font-awesome-icon fixed-width icon="users" />
                Staff
              </b-dropdown-item>
              <b-dropdown-item
                v-if="$can('manage', 'rota.rota-agencies')"
                :to="{ name: 'setup/RotaAgencyList' }"
              >
                <font-awesome-icon fixed-width icon="hospital-user" />
                Agencies
              </b-dropdown-item>
            </b-nav-item-dropdown>
          </ul>
        </div>

        <button
          v-if="showQrFeature && authStore.isSignedIn"
          type="button"
          class="btn btn-outline-secondary open-qr"
          @click="openQrScanner"
        >
          <font-awesome-icon fixed-width icon="qrcode" />
        </button>
        <ul v-if="authStore.isSignedIn" class="navbar-nav d-flex flex-row">
          <li
            v-if="shiftCoverRequestsEnabled && $can('update', 'rota.rotas')"
            class="nav-item d-xl-none me-1"
          >
            <router-link
              class="btn nav-link"
              :to="{ name: 'rota/RotaShiftCover' }"
              active-class=""
            >
              <shift-cover-approvals-badge icon />
            </router-link>
          </li>
          <nav-unread-messages
            :unread-messages="messagingStore.unreadMessages"
          />
          <nav-user-dropdown
            v-if="authStore.isSignedIn"
            :user="authStore.user"
            :app-badge-counts="authStore.navUserDropdownAppBadgeCounts"
            @logout-clicked="doLogout"
          />
        </ul>
      </div>
    </nav>
  </div>
</template>

<script>
import { watch } from "vue";
import { formatISO } from "date-fns";
import get from "lodash/get";
import { useRoute } from "vue-router";
import {
  useAuthStore,
  NavUserDropdown,
  useLibMessagingStore,
  NavUnreadMessages,
} from "@switchsystems/coactive-vue-lib";
import { Hub } from "aws-amplify/utils";
import usePopupActivity from "@/components/activity/usePopupActivity";
import { isFeatureEnabled, FLAG_2393_SHIFT_COVER } from "@/flags.js";
import TopNavRotaDropdown from "./TopNavRotaDropdown.vue";
import ShiftCoverApprovalsBadge from "./rota/shift/ShiftCoverApprovalsBadge.vue";

export default {
  components: {
    NavUserDropdown,
    NavUnreadMessages,
    TopNavRotaDropdown,
    ShiftCoverApprovalsBadge,
  },
  setup() {
    const authStore = useAuthStore();
    const messagingStore = useLibMessagingStore();

    const { showAddActivityPopup } = usePopupActivity();

    const showQrFeature =
      get(import.meta.env, "VITE_FEATURE_1181", "true") === "true";

    function openQrScanner() {
      Hub.dispatch("qr", { event: "show" });
    }

    const route = useRoute();
    watch(route, () => {
      document.querySelector(".navbar-toggler[aria-expanded=true]")?.click();
    });

    function doLogout() {
      Hub.dispatch("authCustom", { event: "doLogout" });
    }

    return {
      formatISO,
      openQrScanner,
      showQrFeature,
      doLogout,
      authStore,
      messagingStore,
      showAddActivityPopup,
      shiftCoverRequestsEnabled: isFeatureEnabled(FLAG_2393_SHIFT_COVER),
    };
  },
};
</script>

<style lang="scss">
#top-nav nav {
  background-color: #daf4bf;
  border-bottom: 4px solid #8ea476;

  .navbar-toggler {
    border: none;
    font-size: unset;
  }

  .open-qr {
    display: block;
    margin-left: auto;
    border: none;
  }
}

@include media-breakpoint-down(sm) {
  #top-nav > nav {
    min-height: $navheight-mobile;
    padding-bottom: 0;
    padding-top: 0;
    border: none;
  }
}

@include media-breakpoint-up(md) {
  #top-nav > nav {
    min-height: $navheight;
  }
}

@include media-breakpoint-up(lg) {
  .open-qr {
    display: none;
  }
}
</style>
